// Angular
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom, map, Observable } from 'rxjs';
// State
import { Select, Store } from '@ngxs/store';
import {
	LastConnection,
	RegisterChannel,
	RegisterEventChannelAction,
	SaveConnectionID,
	SetAvailabilityConnection,
	SetOfflineConnection,
	UnregisterChannel,
} from '../action/signal-r.action';
import { HubConnectionState } from '@microsoft/signalr';
import { UserContextState } from '../../auth/user-context.state';
// Model
import { UserContextModel } from '../../auth/user-context.model';
import { ConfigFileModel } from '../../model/config-file.model';
import { SIGNAL_STATUS, ChannelConfigModel } from '../model/signal-r.model';
// Service
import { AppService } from '../../service/app.service';
import { ConfigService } from '../../service/config.service';
import { LoggerService } from '../../service/logger.service';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { AppInformationService } from '../../service/app-info.service';
// Signal R
import * as signalR from '@microsoft/signalr';

@Injectable({
	providedIn: 'root',
})
export class SignalRService {
	@Select(UserContextState.getUserContext) userContext$: Observable<any>;
	private hubConnection: signalR.HubConnection;
	private currentUser: UserContextModel;

	private lastConfigFile = null;

	constructor(
		private http: HttpClient,
		private configService: ConfigService,
		private store: Store,
		private loggerService: LoggerService,
		private oidcSecurityService: OidcSecurityService,
		private appInfo: AppInformationService,
		private appService: AppService
	) {}

	/**
	 * Open the Hub Connection and attach the events to State
	 * @param module
	 */
	public startConnection(module: string = null): void {
		this.currentUser = this.store.selectSnapshot((state) => state.user);
		const signalRState = this.store.selectSnapshot((state) => state.signalR);

		// Try new connection if the user reload the page and you are logged into the System
		if (this.currentUser && this.currentUser.token) {
			// if connection is OFFLINE or the connection is undefined (by a browser refresh)
			if (signalRState.status === SIGNAL_STATUS.OFFLINE || !this.hubConnection || !this.isOnline()) {
				// If the Config File Changes
				this.configService.getConfig().subscribe(async (configFile: ConfigFileModel): Promise<void> => {
					this.loggerService.log('JTW: Config File Changed');
					this.lastConfigFile = configFile;
					await this.createHubConnection(module);
				});
				// if the token expired or changes, ensure we are online
				this.userContext$.subscribe(async (userContextModel: UserContextModel): Promise<void> => {
					// Wait in case the Config has changed, so we do not have a double connection
					if (userContextModel && this.currentUser) {
						setTimeout(async () => {
							if (
								userContextModel.token &&
								userContextModel.token !== this.currentUser.token &&
								!this.isOnline()
							) {
								this.loggerService.log('JTW: User Token Changed');
								this.lastConfigFile = await lastValueFrom(this.configService.getConfig());
								await this.createHubConnection(module);
							}
						}, 2500);
					}
				});
			}
		}
	}

	/**
	 * Disables the current connection by stopping the hub connection if it exists
	 * and dispatching appropriate actions to update the connection state in the store.
	 *
	 * @return {void} No return value.
	 */
	public disableConnection(): void {
		this.store.dispatch(new SetAvailabilityConnection(false));
		if (this.hubConnection) {
			this.hubConnection
				.stop()
				.then(() => {
					this.store.dispatch(new SetOfflineConnection());
					this.loggerService.log('Connection stopped and disabled');
				})
				.catch((e) => console.warn(e));
		}
	}

	/**
	 * Create the HUB Connection with the server
	 * @param module
	 * @private
	 */
	private async createHubConnection(module: string): Promise<any> {
		let token: string = await lastValueFrom(this.getToken());

		if (!token || token === '') {
			token = this.oidcSecurityService.getAccessToken();
		}

		const url = this.setUrlByRole(module, this.currentUser, this.lastConfigFile);
		if (url) {
			this.hubConnection = new signalR.HubConnectionBuilder()
				.withUrl(url, {
					accessTokenFactory: () => token,
					skipNegotiation: true,
					transport: signalR.HttpTransportType.WebSockets,
				})
				.build();
			this.hubConnection
				.start()
				.then(() => {
					this.loggerService.log('Connection started');
					this.store.dispatch(new LastConnection(Date.now()));
					this.initializeSignalR(module);
				})
				.catch((err) => {
					this.store.dispatch(new SetOfflineConnection());
					this.loggerService.log('Error while starting connection: ' + err);
					setTimeout(() => {
						this.createHubConnection(module);
					}, 3000);
				});
		}
	}

	/**
	 * set the url to connection depending on the module.
	 */
	private setUrlByRole(module: string, user: any, config: any): string {
		return this.appService.getSignalRUrl(module, this.appInfo.currentUrl, user, config);
	}

	/**
	 * Stops the connection after logout
	 */
	public stopConnection(): void {
		if (this.hubConnection) {
			this.hubConnection
				.stop()
				.then(() => {
					this.store.dispatch(new SetOfflineConnection());
					this.loggerService.log('Connection stopped');
				})
				.catch((e) => console.warn(e));
		}
	}

	/**
	 * Attach specific Listeners
	 * @param module
	 * @private
	 */
	private initializeSignalR(module: string): void {
		// Reconnect if Connection Lost
		this.hubConnection.onclose((data) => {
			this.store.dispatch(new SetOfflineConnection());
			this.loggerService.log('Connection Lost. Reconnecting...', data);
			setTimeout(async () => {
				await this.createHubConnection(module);
			}, 3000);
		});

		try {
			const connectionIdApps = this.appService.getSignalRConnectionIdApps();
			if (connectionIdApps.some((path) => this.appInfo.currentUrl.includes(path))) {
				this.hubConnection
					.invoke('GetConnectionId')
					.then((data) => {
						this.store.dispatch(new SaveConnectionID(data));
						this.loggerService.log('New Connection Id:', data);
					})
					.catch((e) => console.warn(e));
			}
		} catch (e) {
			// It is part only of one product
		}

		// Listen to all incoming events
		this.hubConnection.on('OnEvent', (channelName, eventData) => {
			this.loggerService.log(`On Event Fired channel '${channelName}':`, eventData);

			const canStoreEventsData = {
				data: eventData.data,
				user: this.currentUser,
			};

			if (this.appService.canSignalRStoreEvents(canStoreEventsData)) {
				this.store.dispatch(
					new RegisterEventChannelAction(channelName, {
						event: eventData.name,
						data: eventData.data,
					})
				);
				return true;
			}

			const signalExceptionEvents = this.appService.getSignalExceptionEvents();
			if (signalExceptionEvents.find((e) => e === eventData.name)) {
				this.store.dispatch(
					new RegisterEventChannelAction(channelName, {
						event: eventData.name,
						data: eventData.data,
					})
				);
				return true;
			}
			return false;
		});

		this.invokeHubConnection();
	}

	/**
	 * Register a new Channel to listen
	 * @param channelName
	 * @param ChannelConfigModel
	 */
	public registerChannel(
		channelName: string,
		channelConfigModel: ChannelConfigModel = new ChannelConfigModel()
	): void {
		const token = this.store.selectSnapshot((state) => state.user.token);
		if (token) {
			const channels = this.store.selectSnapshot((state) => state.signalR.channels);
			if (!channels[channelName]) {
				this.store.dispatch(new RegisterChannel(channelName, channelConfigModel)).subscribe(() => {
					this.invokeHubConnection();
				});
			}
		}
	}

	/**
	 * UnRegister a channel from the storage and the events
	 * @param channelName
	 */
	public unregisterChannel(channelName: string): void {
		this.store.dispatch(new UnregisterChannel(channelName));
		this.hubConnection.off(channelName);
	}

	/**
	 * Attach any Listener required
	 */
	private invokeHubConnection(): void {
		if (this.isOnline()) {
			const channels = this.store.selectSnapshot((state: any) => {
				return state.signalR.channels;
			});
			for (const channel in channels) {
				if (channels[channel]) {
					// Subscribe to the channels
					this.hubConnection
						.invoke('Subscribe', channel)
						.then((data) => {
							this.loggerService.log('On Order Subscription:', data);
						})
						.catch((e) => console.warn(e));
				}
			}
		}
	}

	/**
	 * public method that invokes a channel that is not registered in the local storage
	 * this is just for the convenience of calling any channel that is not listing any change
	 * @param channel
	 * @param payload
	 */
	public invokeHubPublic(channel: string, payload: string): Promise<any> {
		return new Promise((resolve: any, reject: any) => {
			if (this.isOnline()) {
				this.hubConnection
					.invoke(channel, payload)
					.then((data: any) => resolve(data))
					.catch(() => {
						this.loggerService.log('Connection Lost or Disconnected by closing early');
						return reject([]);
					});
			} else {
				return reject([]);
			}
		});
	}

	/**
	 * Verify the Connection is online
	 */
	public isOnline(): boolean {
		return (
			this.hubConnection && this.hubConnection['connection']['_connectionState'] === HubConnectionState.Connected
		);
	}

	/**
	 * Get short living token from UserApi
	 *
	 * @returns
	 */
	private getToken(): Observable<string> {
		const url = `${this.configService.api.identitySystemService}utils/signal-jwt`;

		return this.http.post(url, {}).pipe(
			map((response: any) => {
				return response.token;
			})
		);
	}
}
